import { useEffect, useMemo, useState } from 'react';
import { intersection } from 'lodash';
import VirtualList from 'rc-virtual-list';
// Utils
import { secondToTimeString } from 'utils/timeConverter';
import { EWeekdays, type TWeekdaySelection } from '@timeedit/types/lib/types';
// Components
import { Button, Icon, TEDrawerSection } from '@timeedit/ui-components';
import { Card, Space, Typography } from '@timeedit/ui-components/antd';
// Types
import TimeSlotInput from './TimeSlotInput';
import type { IProps } from './sections.interface';
// Styles
import './TimeSlot.scss';

const TimeSlot = ({ timeRule, onChange, userIsSuper }: IProps) => {
  const [timeSlots, setTimeSlots] = useState<TWeekdaySelection[]>([]);
  const [timeSlotInputOpen, setTimeSlotInputOpen] = useState(false);
  const [editTimeSlotIndex, setEditTimeSlotIndex] = useState<
    number | undefined
  >();

  useEffect(() => {
    setTimeSlots(timeRule.timeSlotSelection || []);
  }, [timeRule]);

  const onDelete = (index: number) => {
    const newTimeSlots = timeSlots.filter((_, i) => i !== index);
    setTimeSlots(newTimeSlots);
    onChange({ key: 'timeSlotSelection', value: newTimeSlots });
  };

  const onClose = () => {
    setTimeSlotInputOpen(false);
    setEditTimeSlotIndex(undefined);
  };

  const handleChange = (
    timeSlot: TWeekdaySelection,
    timeSlotIndex?: number
  ) => {
    if (timeSlotIndex !== undefined) {
      const newTimeSlots = [...timeSlots];
      newTimeSlots[timeSlotIndex] = timeSlot;
      setTimeSlots(newTimeSlots);
      onChange({ key: 'timeSlotSelection', value: newTimeSlots });
    } else {
      const newTimeSlots = [...timeSlots, timeSlot];
      setTimeSlots(newTimeSlots);
      onChange({
        key: 'timeSlotSelection',
        value: newTimeSlots,
      });
    }
  };

  const coversAllWeekdays = useMemo(() => {
    const allWeekdays = Object.keys(EWeekdays);
    const selectedWeekdays = timeSlots.flatMap((slot) => slot.weekdays);
    const intersected = intersection(allWeekdays, selectedWeekdays);
    return intersected.length === allWeekdays.length;
  }, [timeSlots]);

  return (
    <TEDrawerSection label="Time Slots">
      <div className="section-wrapper time-slots">
        <Card title="Time slots" type="inner" style={{ minWidth: 432 }}>
          <VirtualList data={timeSlots} itemKey={'weekdays'}>
            {(timeSlot: TWeekdaySelection, index: number) => {
              return (
                <div className="row">
                  <div className="weekdays" data-testid="time-slot-weekdays">
                    <strong>{timeSlot.weekdays.join(', ')}</strong>
                    {userIsSuper && (
                      <Space>
                        <Button
                          icon={<Icon id="EditOutlined" alt="Edit" />}
                          type="text"
                          size="small"
                          onClick={() => {
                            setTimeSlotInputOpen(true);
                            setEditTimeSlotIndex(index);
                          }}
                        />
                        <Button
                          icon={<Icon id="DeleteOutlined" alt="Delete" />}
                          type="text"
                          size="small"
                          onClick={() => onDelete(index)}
                        />
                      </Space>
                    )}
                  </div>
                  <div className="times">
                    {timeSlot.times
                      .map(
                        (time) =>
                          `${secondToTimeString(
                            time.clockTimeStart,
                            'HH:mm'
                          )}-${secondToTimeString(time.clockTimeEnd, 'HH:mm')}`
                      )
                      .join(', ')}
                  </div>
                </div>
              );
            }}
          </VirtualList>
          {userIsSuper && (
            <div className="add-btn" data-testid="add-time-slot">
              <Typography.Link
                disabled={coversAllWeekdays}
                onClick={() => {
                  setTimeSlotInputOpen(true);
                }}
              >
                Add
              </Typography.Link>
            </div>
          )}
        </Card>
        <TimeSlotInput
          open={timeSlotInputOpen}
          onClose={onClose}
          timeSlotIndex={editTimeSlotIndex}
          timeSlots={timeSlots}
          onChange={handleChange}
        />
      </div>
    </TEDrawerSection>
  );
};

export default TimeSlot;
